import { useConfig } from "../context/config-context";

/**
 * PrescriberLayout — Layout public pour les pages /prescriber-access
 *
 * Structure :
 *   - Fond #F6F7F9 plein écran
 *   - Header fin avec séparateur (logo centré)
 *   - Zone centrale : card blanche centrée avec le contenu
 *   - Logo preset en bas à gauche (position fixe) — sauf si wide=true
 *   - Footer optionnel sous la card (séparateur + contenu footer)
 *
 * Props :
 *   children    — contenu principal
 *   footer      — élément JSX affiché sous le séparateur en bas de card (optionnel, ignoré si wide=true)
 *   wide        — si true, pas de card, contenu pleine largeur (max-w-6xl), logo preset masqué
 *   banner      — élément JSX affiché entre le header et la card (bandeaux d'alerte)
 */
function PrescriberLayout({ children, footer, wide = false, banner, fullWidth = false }) {
  const siteConfig = useConfig();

  return (
    <div className="relative min-h-screen flex flex-col" style={{ backgroundColor: "#F6F7F9" }}>

      {/* Header */}
      <header className="bg-white">
        <div className="flex items-center justify-center px-6 py-3">
          <img
            src={siteConfig.medias.logo.path}
            alt={siteConfig.medias.logo.alt}
            className="h-10 object-contain"
          />
        </div>
        <div className="h-px w-full" style={{ backgroundColor: "#D3D7DA" }} />
      </header>

      {/* Banner (alerte absolute sous le header, pleine largeur) */}
      {banner && (
        <div className="absolute left-0 right-0 z-10 mx-4 md:mx-6 mt-4 md:mt-6" style={{ top: "65px" }}>
          {banner}
        </div>
      )}

      {/* Zone centrale */}
      {wide ? (
        /* Mode wide : pas de card, contenu pleine largeur */
        <main className="flex-1 p-6">
          {children}
        </main>
      ) : (
        /* Mode normal : card blanche centrée */
        <main className="flex-1 flex items-start justify-center px-3 md:px-4 pt-6 pb-5 md:pb-10">
          <div className={`w-full ${fullWidth ? "max-w-full" : "max-w-[940px]"}`}>
            <div className="overflow-hidden rounded-[15px] md:shadow-[0px_5px_12px_1px_#C2C2C240]">
              {/* Contenu */}
              <div className={`md:bg-white p-5 md:p-8 ${footer ? "pb-28 md:pb-8" : ""}`}>
                {children}
              </div>

              {/* Footer */}
              {footer && (
                <>
                  {/* Séparateur — desktop uniquement */}
                  <div className="hidden md:block h-px mx-8" style={{ backgroundColor: "#D3D7DA" }} />

                  {/* Footer desktop */}
                  <div className="hidden md:block bg-white px-8 py-4">
                    {footer}
                  </div>

                  {/* Footer mobile — sticky en bas, pleine largeur */}
                  <div className="md:hidden fixed bottom-0 left-0 right-0 md:bg-white px-4 pb-6 pt-3 z-20">
                    {footer}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      )}

      {/* Logo preset — bas à gauche, fixe (desktop uniquement, masqué en mode wide) */}
      {!wide && (
        <div className="hidden md:block fixed bottom-0 left-0 pointer-events-none -z-10">
          <img
            src={siteConfig.medias.logo_preset.path}
            alt={siteConfig.medias.logo_preset.alt}
            className="w-40 object-contain"
          />
        </div>
      )}

    </div>
  );
}

export default PrescriberLayout;
